// --- board websocket hook ---
import { useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"

type BoardEvent = {
  type:
    | "card:created"
    | "card:updated"
    | "card:deleted"
    | "column:created"
    | "column:updated"
    | "column:deleted"
    | "comment:created"
    | "comment:deleted"
    | "checklist:updated"
  boardId: string
  payload: unknown
}

// connect to the board ws and invalidate queries on events
export function useBoardSocket(boardId: string | undefined) {
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMounted = useRef(true)

  useEffect(() => {
    if (!boardId) return
    isMounted.current = true

    function connect() {
      if (!isMounted.current) return

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
      const ws = new WebSocket(`${protocol}//${window.location.host}/api/ws/board/${boardId}`)
      wsRef.current = ws

      ws.onmessage = (evt) => {
        try {
          const event: BoardEvent = JSON.parse(evt.data)
          handleEvent(event)
        } catch {
          // Ignore malformed messages
        }
      }

      // reconnect after 3 seconds
      ws.onclose = () => {
        if (!isMounted.current) return
        reconnectTimer.current = setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        ws.close()
      }
    }

    function handleEvent(event: BoardEvent) {
      switch (event.type) {
        case "card:created":
        case "card:updated":
        case "card:deleted":
        case "column:created":
        case "column:updated":
        case "column:deleted":
        // invalidate the whole board, triggers a single refetch
          queryClient.invalidateQueries({ queryKey: ["board", boardId] })
          break
        case "comment:created":
        case "comment:deleted": {
          const payload = event.payload as { cardId?: string }
          if (payload?.cardId) {
            queryClient.invalidateQueries({ queryKey: ["comments", payload.cardId] })
          }
          queryClient.invalidateQueries({ queryKey: ["board", boardId] })
          break
        }
        case "checklist:updated": {
          const payload = event.payload as { cardId?: string }
          if (payload?.cardId) {
            queryClient.invalidateQueries({ queryKey: ["card", payload.cardId] })
          }
          queryClient.invalidateQueries({ queryKey: ["board", boardId] })
          break
        }
      }
    }

    connect()

    return () => {
      isMounted.current = false
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      if (wsRef.current) {
        // prevent reconnect loop on intentional close
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [boardId, queryClient])
}
